<template>
  <b-row class="justify-content-center m-0">
    <div v-if="loading" class="d-flex justify-content-center p-4">
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <b-col v-else class="page__assess-evidence">
      <b-col class="p-0 container__header">
        <h5 v-if="learningUnit" class="display-4 font-weight-bold mb-3 header__lu-title">
          {{ learningUnit.name }}
        </h5>

        <div v-if="learningUnit.level_type" class="text-primary font-weight-bold my-4">
          <span class="mr-1">
            <img
              :src="
                learningUnit.level_type === 'CAREER_SPECIFIC'
                  ? require('@/assets/learning-unit-types/specific.svg')
                  : require('@/assets/learning-unit-types/academic.svg')
              "
              style="height: 16px"
            />
          </span>
          <span class="text__mc-type">{{
            learningUnit.level_type === 'CAREER_SPECIFIC' ? 'Career Specific' : 'Academic Focused'
          }}</span>
        </div>

        <base-back-to-previous-button :previous-path="previousPath" />

        <div class="mt-u6 font-weight-bold">View submission and feedback</div>
      </b-col>

      <b-row
        class="w-100 mt-u5 container__details"
        :class="{ 'custom-grid-layout': !readonly && isAssessor }"
      >
        <!-- left-column -->
        <b-col v-if="!readonly && isAssessor" class="p-0 navcontainer">
          <AssessmentNavigator
            v-if="isLargeScreen"
            class="navigator sticky"
            :parts="partPostIds"
            :part-meta-data="partMetaData"
            @navigation-clicked="isShowNavigator = false"
          />
        </b-col>

        <!-- right-column -->
        <b-col class="mb-3 p-0 card__assess-evidence">
          <div
            v-if="!readonly && isAssessor && !isLargeScreen"
            class="sticky container__hamburger w-100"
          >
            <b-button class="button__hamburger" variant="outline-primary" @click="toggleNavigator">
              <font-awesome-icon icon="bars" size="lg" class="icon__hamburger" />
            </b-button>
            <AssessmentNavigator
              v-show="isLargeScreen || isShowNavigator"
              class="navigator sticky mobile-navigator"
              :parts="partPostIds"
              :part-meta-data="partMetaData"
              @navigation-clicked="isShowNavigator = false"
            />
          </div>

          <b-alert :show="!readonly" variant="info" class="mb-4 alert__notice-evaluation w-100">
            Please evaluate all of 3 parts and give feedback.</b-alert
          >

          <evidence-submission-user-status
            v-model="selectedEvidenceSubmissionId"
            :user="learner"
            :learning-unit="learningUnit"
            class="mb-4 w-100"
            :is-show-assessor-manual="canAssessorAssess"
          />

          <attachment-downloader
            v-if="canAssessorAssess"
            :evidence-submission="evidenceSubmission"
            :post-evaluation-assessments="postEvaluationAssessments"
            :learning-unit-name="learningUnit.name"
          >
          </attachment-downloader>

          <learning-unit-score-description-card
            v-if="learningUnit.score_description"
            :learning-unit="learningUnit"
            class="mb-4"
          />

          <post-evaluation-assessment-part
            v-for="(partMeta, index) in partMetaData"
            :id="`card__part-${index + 1}-task-title`"
            :key="`card__part-${index + 1}-task-list`"
            class="mb-3 w-100 obsrevable"
            :part="partMeta"
            :learning-unit="learningUnit"
            :post-ids="partPostIds[partMeta] ? partPostIds[partMeta] : []"
            :editable="false"
            submittable
            assessable
            :disabled="submitting"
            :evidence-submission="evidenceSubmission"
            mode="assess"
            :readonly="readonly"
            :evidence-submitting="onEvidenceSubmitting"
            :allow-assess="isAssessor"
          >
          </post-evaluation-assessment-part>

          <post-evaluation-assessment-submit
            id="post-evaluation-assessment-submit"
            class="mb-4 w-100 observable"
            :assessor-id="userId"
            :learning-unit="learningUnit"
            :evidence-submission="evidenceSubmission"
            :post-evaluation-assessments="postEvaluationAssessments"
            :allow-assess="isAssessor"
            :readonly="readonly"
          >
          </post-evaluation-assessment-submit>

          <confirmation-evaluation-assessment
            v-show="!readonly"
            id="confirmation-evaluation-assessment"
            class="mb-4 w-100 observable"
            :assessor-id="userId"
            :learning-unit="learningUnit"
            :evidence-submission="evidenceSubmission"
            :post-evaluation-assessments="postEvaluationAssessments"
            :allow-assess="isAssessor"
            @assessed="fetchEventLog"
            @isEvidenceSubmitting="(v) => (onEvidenceSubmitting = v)"
          ></confirmation-evaluation-assessment>

          <post-evaluation-assessment-history
            v-if="eventLogs.length"
            class="w-100"
            :items="eventLogs"
            :evidence-submission-id="selectedEvidenceSubmissionId"
            :part-post-ids="partPostIds"
          >
          </post-evaluation-assessment-history>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import { usePostExtractor } from '@/composables/usePostExtractor';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { feedMixin } from '@/mixins/feedMixin';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import LearningUnit from '@/models/LearningUnit';
import Learner from '@/models/Learner';
import Post from '@/models/Post';
import PostEvaluationAssessmentPart from '@/components/Post/PostEvaluationAssessmentPart';
import PostEvaluationAssessmentSubmit from '@/components/Post/PostEvaluationAssessmentSubmit';
import PostEvaluationAssessmentHistory from '@/components/Post/PostEvaluationAssessmentHistory';
import EvidenceSubmissionUserStatus from '@/components/EvidenceSubmission/EvidenceSubmissionUserStatus';
import BaseBackToPreviousButton from '@/components/base-components/BaseBackToPreviousButton';
import EventLog from '@/models/EventLog';
import { orderBy } from 'lodash';
import LearningUnitScoreDescriptionCard from '@/components/LearningUnit/LearningUnitScoreDescriptionCard';
import ConfirmationEvaluationAssessment from '@/components/Post/ConfirmationEvaluationAssessment.vue';
import AssessmentNavigator from '../LearningUnitInfo/components/AssessmentNavigator.vue';
import AttachmentDownloader from '../../components/EvidenceSubmission/AttachmentDownloader.vue';

export default {
  components: {
    PostEvaluationAssessmentPart,
    EvidenceSubmissionUserStatus,
    BaseBackToPreviousButton,
    PostEvaluationAssessmentSubmit,
    PostEvaluationAssessmentHistory,
    LearningUnitScoreDescriptionCard,
    ConfirmationEvaluationAssessment,
    AssessmentNavigator,
    AttachmentDownloader
  },
  mixins: [feedMixin, userInfoMixin],

  props: {
    isReadOnly: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      loading: false,
      isAgreed: false,
      submitting: false,
      onEvidenceSubmitting: false,
      selectedEvidenceSubmissionId: this.$route.params.evidenceSubmissionId,
      eventLogs: [],
      isLargeScreen: false,
      isShowNavigator: false
    };
  },

  computed: {
    previousPath() {
      return new URL('/en/evaluate', process.env.VUE_APP_CATALOG_APP_URL).toString();
    },

    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    learnerId() {
      return Number.parseInt(this.$route.params.learnerId);
    },

    learner() {
      return Learner.find(this.learnerId);
    },

    evidenceSubmissionId() {
      return Number.parseInt(this.selectedEvidenceSubmissionId);
    },

    learningUnit() {
      return LearningUnit.query()
        .with('ultimate_learning_outcome')
        .whereId(this.learningUnitId)
        .first();
    },

    partMetaData() {
      return Object.keys(Post.PART).filter((part) => this.partPostIds[part]?.length > 0);
    },

    evidenceSubmission() {
      return EvidenceSubmission.find(this.evidenceSubmissionId);
    },

    postEvaluationAssessments() {
      const posts = Post.query()
        .where('post_type', 'EVALUATION_ASSESSMENT')
        .where('learning_unit_id', this.learningUnitId)
        .get();

      return posts;
    },

    partPostIds() {
      const { extractPostIdGroupByPart } = usePostExtractor();

      const { postEvaluationAssessments = [] } = this;

      return postEvaluationAssessments.length
        ? extractPostIdGroupByPart(postEvaluationAssessments)
        : {};
    },

    canSubmit() {
      return this.evidenceSubmission ? this.evidenceSubmission.canSubmit && this.isAgreed : false;
    },

    isAssessor() {
      return (
        this.learningUnit.isOwnerOrAssessor(this.userId) &&
        this.evidenceSubmission.isAssessor(this.userId)
      );
    },

    readonly() {
      return this.isReadOnly
        ? this.isReadOnly
        : this.isAssessor
        ? this.isAssessor && this.evidenceSubmission.assessor_reviewed_at !== null
        : true;
    },

    canAssessorAssess() {
      return this.isAssessor && this.evidenceSubmission.isReviewPendingByAssessor && !this.readonly;
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler: 'onRouteChanged'
    },

    selectedEvidenceSubmissionId: 'fetchEventLog'
  },
  mounted() {
    this.updateView();
    window.addEventListener('resize', this.updateView);
  },

  methods: {
    toggleNavigator() {
      this.isShowNavigator = !this.isShowNavigator;
    },
    updateView() {
      this.isLargeScreen = window.innerWidth >= 992;
    },
    async onRouteChanged() {
      // Sync selectedEvidenceSubmissionId with route params
      this.selectedEvidenceSubmissionId = this.$route.params.evidenceSubmissionId;
      await this.fetch();
      await this.fetchEventLog();
    },

    async fetch() {
      this.loading = true;

      await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

      try {
        await EvidenceSubmission.api().fetchAssessor(this.learningUnitId, {
          user: this.learnerId
        });
        if (!this.isReadOnly && !this.isAssessor) {
          this.$router.push({
            name: 'design-planning/info',
            params: { learningUnitId: this.learningUnit.id },
            query: { tab: 'assess', mode: 'assessor' }
          });
        }
      } catch (error) {
        this.$toast('Cannot load your submissions, please try again');
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    },

    async fetchEventLog() {
      const {
        response: { data = [] }
      } = await EventLog.api().fetch({
        learning_unit_id: this.learningUnitId,
        log_group: 'Submission',
        related_id: this.evidenceSubmissionId
      });

      if (data.length) {
        const events = orderBy(data, 'timestamp', 'desc');
        const eventMessages = events.map((event_log) => JSON.parse(event_log.message));

        this.eventLogs = eventMessages
          .filter((message) => message.$metadata.evidenceSubmissionId == this.evidenceSubmissionId)
          .map((message) => ({ message }));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* Common styles */
.alert__notice-evaluation,
.navcontainer {
  max-width: 910px;
  margin: 0 auto;
}

.page__assess-evidence {
  max-width: 1140px;
}

.page__assess-evidence,
.card__assess-evidence {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sticky {
  position: sticky;
  top: 20px;
}

.navigator.mobile-navigator {
  margin-top: 5px;
  border: 1px solid #e9ecef;
  padding: 16px;
  position: absolute;
  top: auto;
  z-index: 10;
}

/* Grid layout */
.custom-grid-layout {
  display: grid;
  grid-template-columns: 1fr;
}

/* Responsive styles */
@media screen and (min-width: 992px) {
  .custom-grid-layout {
    grid-template-columns: 1fr 3fr;
    gap: 40px;
  }

  .sticky {
    top: 40px;
  }
}

/* Hamburger Menu */
.container__hamburger {
  padding-bottom: 20px;
  z-index: 10;
  background-color: #fff;
  box-shadow: -20px 0 #fff, 20px 0 #fff;
}

.container__hamburger::before {
  content: '';
  position: absolute;
  top: -20px; /* ขยายขึ้นด้านบน */
  left: -20px; /* ขยายไปทางซ้าย */
  width: calc(100% + 40px); /* ขยายความกว้าง */
  height: calc(100% + 20px); /* ขยายความสูงเฉพาะด้านบน */
  background-color: #fff;
  z-index: -1;
}

.button__hamburger {
  padding: 8px 20px;
}
</style>
